import type { ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  ViewChild,
  inject,
  input,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';

import { Analytics } from '@core/analytics-types';
import { filterBoolean } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import type { IconName } from '@core/user-interface-legacy';
import {
  InputComponent,
  RtPlatform,
  RtTooltip,
  IconComponent,
  IconColorDirective,
  IconButtonComponent,
  OverlayOriginDirective,
  ConnectedOverlayDirective,
} from '@core/user-interface-legacy';
import { ViewType } from '@recruitee/search-types';

import { SearchPageQueryInputHighlightDirective } from './query-input-highlight.directive';
import { SearchPageQueryInputInfoComponent } from './query-input-info/query-input-info.component';

@Component({
  selector: 'rt-search-page-query-input',
  templateUrl: './query-input.component.html',
  styleUrls: ['./query-input.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.small]': 'isSmall()',
  },
  imports: [
    IconColorDirective,
    IconComponent,
    OverlayOriginDirective,
    RtTooltip,
    SearchPageQueryInputHighlightDirective,
    InputComponent,
    IconButtonComponent,
    ConnectedOverlayDirective,
    SearchPageQueryInputInfoComponent,
    TranslatePipe,
  ],
})
export class SearchPageQueryInputComponent implements OnChanges {
  private readonly cdRef = inject(ChangeDetectorRef);
  public readonly platform = inject(RtPlatform);
  public readonly analytics = inject(Analytics);

  public readonly value = input<string>(undefined!);
  public readonly highlight = input<RegExp>(/((\bAND\b|\bNOT\b|\bOR\b)|\(|\)|\")/gi);
  public readonly view = input<ViewType>(undefined!);
  public readonly infoFields = input<
    Array<{
      icon: IconName;
      label: string;
    }>
  >(undefined!);
  public readonly readMoreLink = input<string>(undefined!);
  public readonly customPlaceholderKey = input<string>(undefined!);
  public readonly isSmall = input<boolean>(false);

  @Output() public valueChange = new EventEmitter<string>();

  public dropdownOpened: boolean = false;
  public isFocus: boolean = false;
  public placeholder: string;

  public ViewType: typeof ViewType = ViewType;

  @ViewChild(InputComponent, { static: true }) private inputComponent: InputComponent;
  @ViewChild('inputEl', { static: true }) private inputElement: ElementRef;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.placeholder = this.getPlaceholder();

    const debouncedValueChange$: Observable<string> = this.valueChange.pipe(
      debounceTime(500),
      takeUntil(this.ngOnDestroy$.asObservable()),
    );

    debouncedValueChange$.subscribe(value => {
      this.view() !== ViewType.AuditLogs &&
        this.analytics.track('[Candidates] FilterBy_InputValue', { length: (value || '').length });
    });

    debouncedValueChange$
      .pipe(
        startWith(this.value()),
        filterBoolean,
        map(value => value.match(this.highlight())),
        filterBoolean,
      )
      .subscribe(operators => {
        operators.forEach(operator => {
          // eslint-disable-next-line default-case
          switch (operator.toLowerCase()) {
            case 'and':
              this.view() !== ViewType.AuditLogs &&
                this.analytics.track('[Candidates] FilterBy_ANDBooleanOperatorUsed');
              break;

            case 'or':
              this.view() !== ViewType.AuditLogs &&
                this.analytics.track('[Candidates] FilterBy_ORBooleanOperatorUsed');
              break;

            case 'not':
              this.view() !== ViewType.AuditLogs &&
                this.analytics.track('[Candidates] FilterBy_NOTBooleanOperatorUsed');
              break;
          }
        });
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('view' in changes || 'customPlaceholder' in changes) {
      this.placeholder = this.getPlaceholder();
    }
  }

  public getPlaceholder() {
    const customPlaceholderKey = this.customPlaceholderKey();
    if (customPlaceholderKey) return customPlaceholderKey;

    switch (this.view()) {
      case ViewType.Candidates:
        return 'app.new_search_page.placeholders.candidates';
      case ViewType.AuditLogs:
        return 'app.settings.audit_logs.search_placeholder';
      case ViewType.Hires:
        return 'app.new_search_page.placeholders.hires';
      default:
        return 'app.new_search_page.placeholders.candidates';
    }
  }

  public toggleDropdown(): void {
    this.dropdownOpened = !this.dropdownOpened;
    this.cdRef.detectChanges();
  }

  public emitValueChange(value: string): void {
    this.valueChange.emit(value);
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public setFocusStatus(status: boolean, event?: FocusEvent): void {
    if (!event?.relatedTarget) {
      this.isFocus = status;
      this.cdRef.detectChanges();
    }
  }

  public setFocus(): void {
    if (this.inputComponent) {
      this.inputComponent.setFocus();
    } else if (this.inputElement && this.inputElement.nativeElement) {
      this.inputElement.nativeElement.focus();
    }
  }
}
